import { useCallback, useEffect, useState } from 'react';
import { listGenomes, listOrganisms, spawnOrganism, killOrganism, snapshotOrganism } from '../services/api';
import type { Organism } from '../types/simulation';
import { useNotification } from '../contexts/NotificationContext';
import './OrganismPanel.css';

interface OrganismDetailProps {
  org: Organism;
  isFollowing: boolean;
  snapshotting: boolean;
  onKill: () => void;
  onSnapshot: () => void;
  onFollow: () => void;
}

function OrganismDetail({ org, isFollowing, snapshotting, onKill, onSnapshot, onFollow }: OrganismDetailProps) {
  const telemetry = org.telemetry ?? {};
  const gestationProgress =
    org.state === 'gestating' && telemetry.morphogen_pressure != null
      ? (1 - telemetry.morphogen_pressure) * 100
      : null;
  const energyProgress =
    telemetry.energy_reserves != null ? telemetry.energy_reserves * 100 : null;

  return (
    <div className="organism-detail">
      {gestationProgress != null && (
        <div className="org-stat">
          <span className="org-stat-label">gestation</span>
          <div className="org-progress-bar">
            <div className="org-progress-fill org-progress-fill--gestation" style={{ width: `${gestationProgress}%` }} />
          </div>
        </div>
      )}
      {energyProgress != null && (
        <div className="org-stat">
          <span className="org-stat-label">energy</span>
          <div className="org-progress-bar">
            <div className="org-progress-fill org-progress-fill--energy" style={{ width: `${energyProgress}%` }} />
          </div>
        </div>
      )}
      {gestationProgress == null && energyProgress == null && (
        <div className="org-detail-empty">no telemetry yet</div>
      )}
      <div className="org-stat">
        <span className="org-stat-label">genome</span>
        <span className="org-genome-id">{org.genome_id}</span>
      </div>
      <div className="organism-actions">
        <button className={`follow-btn${isFollowing ? ' follow-btn--active' : ''}`} onClick={onFollow} title={isFollowing ? 'Stop following' : 'Follow organism'}>
          follow
        </button>
        <button className="snapshot-btn" onClick={onSnapshot} disabled={snapshotting} title="Snapshot organism">
          {snapshotting ? '…' : 'snapshot'}
        </button>
        <button className="kill-btn" onClick={onKill} title="Kill organism">
          kill
        </button>
      </div>
    </div>
  );
}

interface OrganismPanelProps {
  simulationId: string;
  devMode?: boolean;
  selectedEntityId?: string;
  hoveredEntityId?: string;
  snapToEntityId?: string;
  dismissSignal?: number;
  tabMode?: boolean;
  onOrganismSelect?: (entityId: string | undefined) => void;
  onOrganismHover?: (entityId: string | undefined) => void;
  onSnapTo?: (entityId: string | undefined) => void;
}

export function OrganismPanel({ simulationId, devMode = true, selectedEntityId, hoveredEntityId, snapToEntityId, dismissSignal, tabMode = false, onOrganismSelect, onOrganismHover, onSnapTo }: OrganismPanelProps) {
  const { showNotification } = useNotification();
  const [organisms, setOrganisms] = useState<Organism[]>([]);
  const [genomeIds, setGenomeIds] = useState<string[]>([]);
  const [selectedGenome, setSelectedGenome] = useState<string>('');
  const [offline, setOffline] = useState(false);
  const [spawning, setSpawning] = useState(false);
  const [killingAll, setKillingAll] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(() => tabMode ? false : window.matchMedia('(max-width: 900px)').matches);
  const [snapshottingIds, setSnapshottingIds] = useState<Set<string>>(new Set());

  // Collapse list when canvas is tapped (not in tabMode — App handles that)
  useEffect(() => {
    if (dismissSignal && !tabMode) setListCollapsed(true);
  }, [dismissSignal, tabMode]);

  // Open the list and scroll to newly selected organisms
  useEffect(() => {
    if (selectedEntityId) setListCollapsed(false);
  }, [selectedEntityId]);

  const toggleExpanded = (id: string) => {
    onOrganismSelect?.(id === selectedEntityId ? undefined : id);
  };

  const refreshOrganisms = useCallback(async () => {
    try {
      const data = await listOrganisms(simulationId, true);
      setOrganisms(data);
      setOffline(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('502')) {
        setOffline(true);
      } else {
        console.error('Failed to load organisms:', err);
      }
    }
  }, [simulationId]);

  // Initial + polling refresh of organisms
  useEffect(() => {
    refreshOrganisms();
    const id = setInterval(refreshOrganisms, 3000);
    return () => clearInterval(id);
  }, [refreshOrganisms]);

  // Reload genome list when devMode changes
  useEffect(() => {
    let cancelled = false;
    listGenomes(simulationId, devMode)
      .then((res) => {
        if (cancelled) return;
        setGenomeIds(res.genome_ids);
        setSelectedGenome(res.genome_ids.length > 0 ? res.genome_ids[0] : '');
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load genomes:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [simulationId, devMode]);

  const handleSpawn = async () => {
    if (!selectedGenome || spawning) return;
    setSpawning(true);
    try {
      await spawnOrganism(simulationId, { genome_id: selectedGenome });
      showNotification(`Spawned ${selectedGenome}`);
      await refreshOrganisms();
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Spawn failed');
    } finally {
      setSpawning(false);
    }
  };

  const handleKill = async (entityId: string) => {
    if (!confirm(`Kill organism ${entityId.substring(0, 8)}?`)) return;
    try {
      await killOrganism(simulationId, entityId);
      showNotification('Organism terminated');
      await refreshOrganisms();
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Kill failed');
    }
  };

  const handleSnapshot = async (entityId: string) => {
    setSnapshottingIds(s => new Set(s).add(entityId));
    try {
      await snapshotOrganism(simulationId, entityId);
      showNotification('Snapshot saved');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Snapshot failed');
    } finally {
      setSnapshottingIds(s => { const n = new Set(s); n.delete(entityId); return n; });
    }
  };

  const handleKillAll = async () => {
    if (organisms.length === 0) return;
    if (!confirm(`Kill all ${organisms.length} organism${organisms.length === 1 ? '' : 's'}?`)) return;
    setKillingAll(true);
    try {
      await Promise.all(organisms.map((o) => killOrganism(simulationId, o.entity_id)));
      showNotification(`Killed ${organisms.length} organism${organisms.length === 1 ? '' : 's'}`);
      await refreshOrganisms();
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Kill all failed');
    } finally {
      setKillingAll(false);
    }
  };

  return (
    <div className={`organism-panel${listCollapsed ? ' organism-panel--collapsed' : ''}`}>
      <div className="organism-spawn">
        <div className="spawn-header">
          <h3>Spawn</h3>
        </div>
        <select
          className="genome-select"
          value={selectedGenome}
          onChange={(e) => setSelectedGenome(e.target.value)}
          disabled={genomeIds.length === 0 || spawning || offline}
        >
          {genomeIds.length === 0 && <option value="">No genomes</option>}
          {genomeIds.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
        <div className="spawn-actions">
          <button
            className="kill-all-btn"
            onClick={handleKillAll}
            disabled={killingAll || offline || organisms.length === 0}
            title="Kill all live organisms"
          >
            {killingAll ? '…' : '✕'}
          </button>
          <button
            className="spawn-btn"
            onClick={handleSpawn}
            disabled={!selectedGenome || spawning || offline}
          >
            {spawning ? 'spawning...' : '+ spawn'}
          </button>
        </div>
      </div>

      {!tabMode && (
        <div className="organism-panel-header" onClick={() => setListCollapsed((c) => !c)}>
          <h3>Organisms ({organisms.length})</h3>
          <div className="organism-list-controls">
            <button
              onClick={(e) => { e.stopPropagation(); refreshOrganisms(); }}
              className="refresh-btn"
              title="Refresh organism list"
            >
              ↻
            </button>
            <span className={`organism-list-chevron${listCollapsed ? '' : ' open'}`}>▸</span>
          </div>
        </div>
      )}

      <div className={`organisms${!tabMode && listCollapsed ? ' organisms--collapsed' : ''}`}>
        {offline && (
          <div className="no-organisms">Sim offline</div>
        )}

        {!offline && organisms.length === 0 && (
          <div className="no-organisms">No active organisms</div>
        )}

        {!offline && organisms.map((org) => {
          const expanded = org.entity_id === selectedEntityId;
          return (
            <div key={org.entity_id} className={`organism-item state-${org.state}${expanded ? ' expanded' : ''}${org.entity_id === selectedEntityId ? ' selected' : ''}${org.entity_id === hoveredEntityId ? ' hovered' : ''}`} onMouseEnter={() => onOrganismHover?.(org.entity_id)} onMouseLeave={() => onOrganismHover?.(undefined)}>
              <div className="organism-header" onClick={() => toggleExpanded(org.entity_id)}>
                <div className="organism-name">
                  <span className={`organism-chevron${expanded ? ' open' : ''}`}>▸</span>
                  <span className="organism-id" title={org.entity_id}>
                    {org.entity_id.substring(0, 8)}
                  </span>
                  <span className={`state-badge state-badge--${org.state}`}>
                    {org.state}
                  </span>
                </div>
              </div>
              {expanded && (
                <OrganismDetail
                  org={org}
                  isFollowing={snapToEntityId === org.entity_id}
                  snapshotting={snapshottingIds.has(org.entity_id)}
                  onKill={() => handleKill(org.entity_id)}
                  onSnapshot={() => handleSnapshot(org.entity_id)}
                  onFollow={() => { onSnapTo?.(snapToEntityId === org.entity_id ? undefined : org.entity_id); setListCollapsed(true); }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
